import type {
  BookingItemTireQuoteReevaluateApiData,
  BookingItemTireQuoteReevaluateApiResponses,
  BookingProposalCloseApiData,
  BookingProposalCloseApiResponses,
  BookingProposalGetApiData,
  BookingProposalGetApiResponses,
  BookingProposalPatchApiData,
  BookingProposalPatchApiResponses,
  BookingProposalRejectApiData,
  BookingProposalRejectApiResponses,
  BookingShoppingCartForNewBookingApiData,
  BookingShoppingCartForNewBookingApiResponses,
  CarTireQuoteItemTirePatchApiData,
  CarTireQuoteItemTirePatchApiResponses,
  CarTireQuoteOptionCreateApiData,
  CarTireQuoteOptionCreateApiResponses,
  CarTireQuoteOptionDeleteApiData,
  CarTireQuoteOptionDeleteApiResponses,
  CarTireQuoteOptionSwapApiData,
  CarTireQuoteOptionSwapApiResponses,
  CarTireQuoteOverviewApiData,
  CarTireQuoteOverviewApiResponses,
  CarTireQuoteSelectApiData,
  CarTireQuoteSelectApiResponses,
  CarTireQuoteSelectedItemApiData,
  CarTireQuoteSelectedItemApiResponses,
  CarTireQuoteSuggestionApiData,
  CarTireQuoteSuggestionApiResponses,
  DiscountsForShoppingCartApiData,
  DiscountsForShoppingCartApiResponses,
  EdiwheelPlaceOrderForQuoteApiData,
  EdiwheelPlaceOrderForQuoteApiResponses,
  ExternalModelGroupListApiData,
  ExternalModelGroupListApiResponses,
  RecallProposalRejectApiData,
  RecallProposalRejectApiResponses,
  RecallProposalsPublishApiData,
  RecallProposalsPublishApiResponses,
  RecallProposalStatsApiData,
  RecallProposalStatsApiResponses,
  ServiceDepartmentIsShopClosedApiData,
  ServiceDepartmentIsShopClosedApiResponses,
  ShopTireQuoteGetBySlugApiData,
  ShopTireQuoteGetBySlugApiResponses,
  ShopTireQuoteRejectApiData,
  ShopTireQuoteRejectApiResponses,
  ShopTireQuoteRemindApiData,
  ShopTireQuoteRemindApiResponses,
  ShopTireQuoteSalesItemsListApiData,
  ShopTireQuoteSalesItemsListApiResponses,
  ShopTireQuoteSmsSendApiData,
  ShopTireQuoteSmsSendApiResponses,
  ShopTireQuoteStatusEventListApiData,
  ShopTireQuoteStatusEventListApiResponses,
  ShopTireQuoteViewCreateApiData,
  ShopTireQuoteViewCreateApiResponses,
  TireBrandConfigurationCreateApiData,
  TireBrandConfigurationCreateApiResponses,
  TireBrandConfigurationListApiData,
  TireBrandConfigurationListApiResponses,
  TireBrandConfigurationPatchApiData,
  TireBrandConfigurationPatchApiResponses,
  TireModelConfigurationBulkCreateOrUpdateApiData,
  TireModelConfigurationBulkCreateOrUpdateApiResponses,
  TireModelConfigurationListApiData,
  TireModelConfigurationListApiResponses,
  TireQuoteCreateApiData,
  TireQuoteCreateApiResponses,
  TireQuoteGetApiData,
  TireQuoteGetApiResponses,
  TireQuoteListApiData,
  TireQuoteListApiResponses,
  TireQuotePatchApiData,
  TireQuotePatchApiResponses,
  TireSupplierConfigurationCreateApiData,
  TireSupplierConfigurationCreateApiResponses,
  TireSupplierConfigurationListApiData,
  TireSupplierConfigurationListApiResponses,
  TireSupplierConfigurationPatchApiData,
  TireSupplierConfigurationPatchApiResponses,
  TreadDepthWeeksUntilNextLevelApiData,
  TreadDepthWeeksUntilNextLevelApiResponses
} from '@navio/nsdk';

import { apiUrl, dataEndpoint, responseEndpoint } from '../defineResource';

type RecallProposalStatsWithSmsSubjectFilter = Omit<RecallProposalStatsApiData, 'query'> & {
  query?: NonNullable<RecallProposalStatsApiData['query']> & {
    bookingProposalSmsSubjects?: string[];
  };
};

type RecallProposalsPublishRequest = RecallProposalsPublishApiData['body'] & {
  serviceDepartmentId: RecallProposalsPublishApiData['path']['serviceDepartmentId'];
};

// Tire quotes

// Registered by `impersonationBypass`, not the domain map, so call sites use `...IgnoringImpersonation`.
export const getCarTireQuotesEndpoint = dataEndpoint<TireQuoteListApiData, TireQuoteListApiResponses>({
  getUrl: (queryParams) => apiUrl('car-tire-quotes/', queryParams)
});

export const getTireQuoteUrls = {
  postCreateCarTireQuotes: responseEndpoint<TireQuoteCreateApiData, TireQuoteCreateApiResponses>({
    getUrl: () => apiUrl('car-tire-quotes/'),
    getBody: (data) => data
  }),
  /** The options a new offer for a car and season would hold, by the organization's tire priorities. */
  getCarTireQuoteSuggestion: dataEndpoint<CarTireQuoteSuggestionApiData, CarTireQuoteSuggestionApiResponses>({
    getUrl: (queryParams) => apiUrl('car-tire-quotes/suggestion/', queryParams)
  }),
  getRecallStats: dataEndpoint<RecallProposalStatsWithSmsSubjectFilter, RecallProposalStatsApiResponses>({
    getUrl: ({ serviceDepartmentId, ...queryParams }) => apiUrl(`recalls/${serviceDepartmentId}/stats/`, queryParams)
  }),
  postSendRecallProposalSms: responseEndpoint<
    RecallProposalsPublishApiData,
    RecallProposalsPublishApiResponses,
    RecallProposalsPublishRequest
  >({
    getUrl: ({ serviceDepartmentId }) => apiUrl(`recalls/${serviceDepartmentId}/publish-to-customer/`),
    getBody: ({ serviceDepartmentId: _, ...body }): Partial<RecallProposalsPublishRequest> => body
  }),
  patchRejectRecall: responseEndpoint<RecallProposalRejectApiData, RecallProposalRejectApiResponses>({
    getUrl: ({ bookingProposalId }) => apiUrl(`recalls/${bookingProposalId}/reject/`)
  }),
  patchRejectBookingProposal: responseEndpoint<BookingProposalRejectApiData, BookingProposalRejectApiResponses>({
    getUrl: ({ bookingProposalId }) => apiUrl(`booking-proposals/${bookingProposalId}/reject/`)
  }),
  patchCloseBookingProposal: responseEndpoint<BookingProposalCloseApiData, BookingProposalCloseApiResponses>({
    getUrl: ({ bookingProposalId }) => apiUrl(`booking-proposals/${bookingProposalId}/close/`)
  }),
  patchBookingProposal: responseEndpoint<BookingProposalPatchApiData, BookingProposalPatchApiResponses>({
    getUrl: ({ bookingProposalId }) => apiUrl(`booking-proposals/${bookingProposalId}/`),
    getBody: ({ bookingProposalId: _, ...body }) => body
  }),
  patchCarTireQuoteItemTire: responseEndpoint<CarTireQuoteItemTirePatchApiData, CarTireQuoteItemTirePatchApiResponses>({
    getUrl: ({ pk }) => apiUrl(`car-tire-quote-item-tire/${pk}/`),
    getBody: ({ pk: _, ...body }) => body
  }),
  patchCarTireQuote: responseEndpoint<TireQuotePatchApiData, TireQuotePatchApiResponses>({
    getUrl: ({ pk }) => apiUrl(`car-tire-quotes/${pk}/`),
    getBody: ({ pk: _, ...body }) => body
  }),
  postCarTireQuoteOption: responseEndpoint<CarTireQuoteOptionCreateApiData, CarTireQuoteOptionCreateApiResponses>({
    getUrl: ({ carTireQuoteId }) => apiUrl(`car-tire-quotes/${carTireQuoteId}/quote-items/`),
    getBody: ({ carTireQuoteId: _, ...body }) => body
  }),
  postCarTireQuoteOptionSwap: responseEndpoint<CarTireQuoteOptionSwapApiData, CarTireQuoteOptionSwapApiResponses>({
    getUrl: ({ carTireQuoteItemId }) => apiUrl(`car-tire-quote-item/${carTireQuoteItemId}/swap/`),
    getBody: ({ carTireQuoteItemId: _, ...body }) => body
  }),
  deleteCarTireQuoteOption: dataEndpoint<CarTireQuoteOptionDeleteApiData, CarTireQuoteOptionDeleteApiResponses>({
    getUrl: ({ pk }) => apiUrl(`car-tire-quote-item/${pk}/`)
  }),
  getBookingProposal: dataEndpoint<BookingProposalGetApiData, BookingProposalGetApiResponses>({
    getUrl: ({ bookingProposalId }) => apiUrl(`booking-proposals/${bookingProposalId}/`)
  }),
  getQuoteStatusEvents: dataEndpoint<ShopTireQuoteStatusEventListApiData, ShopTireQuoteStatusEventListApiResponses>({
    getUrl: (queryParams) => apiUrl('shop/tire-quote-status-events/', queryParams)
  }),
  getPaginatedQuoteStatusEvents: dataEndpoint<
    ShopTireQuoteStatusEventListApiData,
    ShopTireQuoteStatusEventListApiResponses
  >({
    getUrl: (queryParams) => apiUrl('shop/tire-quote-status-events/', queryParams)
  }),
  postSendTireQuoteSms: responseEndpoint<ShopTireQuoteSmsSendApiData, ShopTireQuoteSmsSendApiResponses>({
    getUrl: ({ tireQuoteId }) => apiUrl(`car-tire-quotes/${tireQuoteId}/sms-send/`),
    getBody: (data) => data
  }),
  getIsShopClosed: dataEndpoint<ServiceDepartmentIsShopClosedApiData, ServiceDepartmentIsShopClosedApiResponses>({
    getUrl: ({ serviceDepartmentId, ...queryParams }) =>
      apiUrl(`service-departments/${serviceDepartmentId}/is-shop-closed/`, queryParams)
  }),
  getTireOffersOverview: dataEndpoint<CarTireQuoteOverviewApiData, CarTireQuoteOverviewApiResponses>({
    getUrl: (queryParams) => apiUrl('car-tire-quotes/overview/', queryParams)
  }),
  getPaginatedTireQuoteSalesItems: dataEndpoint<
    ShopTireQuoteSalesItemsListApiData,
    ShopTireQuoteSalesItemsListApiResponses
  >({
    getUrl: (queryParams) => apiUrl('car-tire-quotes/sales-items/', queryParams)
  }),
  getCarTireQuoteBySlug: dataEndpoint<ShopTireQuoteGetBySlugApiData, ShopTireQuoteGetBySlugApiResponses>({
    getUrl: ({ carTireQuoteSlug }) => apiUrl(`car-tire-quotes/${carTireQuoteSlug}/`)
  }),
  getCarTireQuoteById: dataEndpoint<TireQuoteGetApiData, TireQuoteGetApiResponses>({
    getUrl: ({ pk }) => apiUrl(`car-tire-quotes/${pk}/`)
  }),
  postRejectTireOfferQuote: responseEndpoint<ShopTireQuoteRejectApiData, ShopTireQuoteRejectApiResponses>({
    getUrl: ({ carTireQuoteId }) => apiUrl(`car-tire-quotes/${carTireQuoteId}/reject/`),
    getBody: (data) => data
  }),
  postRemindTireOfferQuote: responseEndpoint<ShopTireQuoteRemindApiData, ShopTireQuoteRemindApiResponses>({
    getUrl: ({ carTireQuoteSlug }) => apiUrl(`car-tire-quotes/${carTireQuoteSlug}/remind/`),
    getBody: (data) => data
  }),
  getPaginatedCarTireQuotes: dataEndpoint<TireQuoteListApiData, TireQuoteListApiResponses>({
    getUrl: (queryParams) => apiUrl('car-tire-quotes/', queryParams)
  }),
  getSelectedQuoteItemInQuote: dataEndpoint<CarTireQuoteSelectedItemApiData, CarTireQuoteSelectedItemApiResponses>({
    getUrl: ({ carTireQuoteId }) => apiUrl(`car-tire-quotes/${carTireQuoteId}/selected-quote-item/`)
  }),
  postConfirmTireQuoteOffer: responseEndpoint<CarTireQuoteSelectApiData, CarTireQuoteSelectApiResponses>({
    getUrl: ({ carTireQuoteItemId }) => apiUrl(`car-tire-quotes/${carTireQuoteItemId}/select/`),
    getBody: (data) => data
  }),
  getTireSetOfferPageView: dataEndpoint<ShopTireQuoteViewCreateApiData, ShopTireQuoteViewCreateApiResponses>({
    getUrl: ({ carTireQuoteSlug }) => apiUrl(`car-tire-quotes/${carTireQuoteSlug}/view/`)
  }),
  getWeeksUntilNextTireLevelWarning: dataEndpoint<
    TreadDepthWeeksUntilNextLevelApiData,
    TreadDepthWeeksUntilNextLevelApiResponses
  >({
    getUrl: ({ carWheelSetId, ...rest }) =>
      apiUrl(`car-wheel-sets/${carWheelSetId}/tread-depth/weeks-until-next-level/`, rest)
  }),
  postGetShoppingCartForNewBooking: responseEndpoint<
    BookingShoppingCartForNewBookingApiData,
    BookingShoppingCartForNewBookingApiResponses
  >({
    getUrl: () => apiUrl('bookings/shopping-cart-for-new-booking/'),
    getBody: (data) => data
  }),
  postGetShoppingCartForNewBookingWithoutAddress: responseEndpoint<
    DiscountsForShoppingCartApiData,
    DiscountsForShoppingCartApiResponses
  >({
    getUrl: () => apiUrl('bookings/discounts-for-shopping-cart/'),
    getBody: (data) => data
  }),
  getPaginatedTireBrandConfigurations: dataEndpoint<
    TireBrandConfigurationListApiData,
    TireBrandConfigurationListApiResponses
  >({
    getUrl: (queryParams) => apiUrl('shop/tire-brand-configurations/', queryParams)
  }),
  postCreateTireBrandConfiguration: responseEndpoint<
    TireBrandConfigurationCreateApiData,
    TireBrandConfigurationCreateApiResponses
  >({
    getUrl: () => apiUrl('shop/tire-brand-configurations/'),
    getBody: (data) => data
  }),
  patchTireBrandConfiguration: responseEndpoint<
    TireBrandConfigurationPatchApiData,
    TireBrandConfigurationPatchApiResponses
  >({
    getUrl: ({ pk }) => apiUrl(`shop/tire-brand-configurations/${pk}/`),
    getBody: ({ pk: _, ...body }) => body
  }),
  getPaginatedTireBrandConfigurationModels: dataEndpoint<
    TireModelConfigurationListApiData,
    TireModelConfigurationListApiResponses
  >({
    getUrl: (queryParams) => apiUrl('shop/tire-model-configurations/', queryParams)
  }),
  getTireBrandConfigurationModels: dataEndpoint<
    TireModelConfigurationListApiData,
    TireModelConfigurationListApiResponses
  >({
    getUrl: (queryParams) => apiUrl('shop/tire-model-configurations/', queryParams)
  }),
  postCreateTireModelConfigurationInBulk: responseEndpoint<
    TireModelConfigurationBulkCreateOrUpdateApiData,
    TireModelConfigurationBulkCreateOrUpdateApiResponses
  >({
    getUrl: () => apiUrl('shop/tire-model-configurations/bulk/'),
    getBody: (data) => data
  }),
  getPaginatedTireInventorySuppliers: dataEndpoint<
    TireSupplierConfigurationListApiData,
    TireSupplierConfigurationListApiResponses
  >({
    getUrl: (queryParams) => apiUrl('shop/tire-supplier-configurations/', queryParams)
  }),
  postCreateTireSupplierConfiguration: responseEndpoint<
    TireSupplierConfigurationCreateApiData,
    TireSupplierConfigurationCreateApiResponses
  >({
    getUrl: () => apiUrl('shop/tire-supplier-configurations/'),
    getBody: (data) => data
  }),
  patchTireSupplierConfiguration: responseEndpoint<
    TireSupplierConfigurationPatchApiData,
    TireSupplierConfigurationPatchApiResponses
  >({
    getUrl: ({ pk }) => apiUrl(`shop/tire-supplier-configurations/${pk}/`),
    getBody: ({ pk: _, ...body }) => body
  }),
  postInventoryPlaceOrder: responseEndpoint<EdiwheelPlaceOrderForQuoteApiData, EdiwheelPlaceOrderForQuoteApiResponses>({
    getUrl: ({ quoteId }) => apiUrl(`shop/inventory-tire-place-order/${quoteId}/`),
    getBody: ({ quoteId: _, ...body }) => body
  }),
  getExternalModelGroups: dataEndpoint<ExternalModelGroupListApiData, ExternalModelGroupListApiResponses>({
    getUrl: (queryParams) => apiUrl('external-model-groups/', queryParams)
  }),
  postBookingItemTireQuoteReevaluate: responseEndpoint<
    BookingItemTireQuoteReevaluateApiData,
    BookingItemTireQuoteReevaluateApiResponses
  >({
    getUrl: ({ bookingItemId }) => apiUrl(`booking-items/${bookingItemId}/tire-quotes/re-evaluate/`),
    getBody: ({ bookingItemId: _, ...body }) => body
  })
};

export { getTireOfferNudgeUrls } from './tireOfferNudgeUrls';
